import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { User } from 'oidc-client-ts';
import { normalizeClaims, type NormalizedClaims, type Role } from '@tips-sso/shared';
import { setCachedAccessToken, userManager } from './userManager.js';
import { oidcConfigured } from './config.js';

export interface AuthIdentity extends NormalizedClaims {
  /** Best-effort display name from the ID token profile. */
  displayName: string;
}

interface AuthState {
  identity: AuthIdentity | null;
  loading: boolean;
  /** True until OIDC config (issuer + client id) is present. */
  configured: boolean;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True if the user holds ANY of the given roles. */
  hasRole: (...roles: Role[]) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** Map an oidc-client-ts User (ID-token profile) into our normalized identity. */
function toIdentity(user: User | null): AuthIdentity | null {
  if (!user || user.expired) return null;
  // user.profile carries the ID-token claims, including the ZITADEL roles URN.
  const claims = normalizeClaims(user.profile as never);
  const displayName =
    (user.profile.name as string | undefined) ??
    (user.profile.preferred_username as string | undefined) ??
    claims.email ??
    claims.subject;
  return { ...claims, displayName };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [loading, setLoading] = useState<boolean>(oidcConfigured);

  useEffect(() => {
    if (!oidcConfigured) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    userManager
      .getUser()
      .then((user) => {
        if (cancelled) return;
        setCachedAccessToken(user?.access_token ?? null);
        setIdentity(toIdentity(user));
      })
      .catch(() => {
        if (!cancelled) setIdentity(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    const onLoaded = (user: User) => {
      setCachedAccessToken(user.access_token ?? null);
      setIdentity(toIdentity(user));
    };
    const onUnloaded = () => {
      setCachedAccessToken(null);
      setIdentity(null);
    };

    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);

    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
    };
  }, []);

  const login = useCallback(async (returnTo?: string) => {
    await userManager.signinRedirect({
      state: { returnTo: returnTo ?? window.location.pathname + window.location.search },
    });
  }, []);

  const logout = useCallback(async () => {
    setCachedAccessToken(null);
    setIdentity(null);
    try {
      await userManager.signoutRedirect();
    } catch {
      // If the OP has no end-session endpoint, at least clear local state.
      await userManager.removeUser();
      window.location.assign('/');
    }
  }, []);

  const hasAnyRole = useCallback(
    (roles: readonly string[]) =>
      identity ? roles.some((r) => identity.roles.includes(r)) : false,
    [identity],
  );

  const hasRole = useCallback(
    (...roles: Role[]) => hasAnyRole(roles),
    [hasAnyRole],
  );

  const value = useMemo<AuthState>(
    () => ({
      identity,
      loading,
      configured: oidcConfigured,
      login,
      logout,
      hasRole,
      hasAnyRole,
    }),
    [identity, loading, login, logout, hasRole, hasAnyRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

function CenteredSpinner() {
  return (
    <div className="route-center">
      <div className="spinner" aria-label="Loading" />
    </div>
  );
}

/** Requires an authenticated session; otherwise starts the OIDC login redirect. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { identity, loading, configured, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && configured && !identity) {
      void login(location.pathname + location.search);
    }
  }, [loading, configured, identity, login, location.pathname, location.search]);

  if (!configured) {
    return (
      <div className="route-center">
        <div className="empty-state">
          <h2>OIDC not configured</h2>
          <p>
            Set <code>VITE_ZITADEL_ISSUER</code> and <code>VITE_ZITADEL_CLIENT_ID</code> to enable
            sign-in.
          </p>
        </div>
      </div>
    );
  }
  if (loading || !identity) return <CenteredSpinner />;
  return <>{children}</>;
}

/** Requires that the session holds at least one of `roles`. */
export function RoleRoute({
  roles,
  children,
}: {
  roles: readonly string[];
  children: ReactNode;
}) {
  const { identity, loading, hasAnyRole } = useAuth();
  if (loading) return <CenteredSpinner />;
  if (!identity) return <Navigate to="/" replace />;
  if (!hasAnyRole(roles)) {
    return (
      <div className="route-center">
        <div className="empty-state">
          <h2>Access denied</h2>
          <p>Your account does not have access to the TipsConnect console.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
